/**
 * Pro unlock logic
 *
 * Early-adopter pricing: first 50 purchases at $19 USD,
 * then regular price at $29 USD.
 *
 * The BCH amount is calculated dynamically from the live BCH/USD price so
 * the merchant always charges exactly the right USD equivalent — no hardcoded
 * BCH amounts that go stale as price moves.
 *
 * The purchase count is read live from the BCH blockchain via public Fulcrum
 * (ElectrumX) servers over WSS — fully verifiable, no server needed.
 */

import { fetchTxCount } from '@/lib/explorer';

export const EARLY_LIMIT        = 50;   // first N purchases at early price
export const EARLY_PRICE_USD    = 19;   // early-adopter price in USD
export const REGULAR_PRICE_USD  = 29;   // regular price in USD

// Transactions on the Pro address before launch — subtracted so the
// purchase count starts at 0 from launch day.
export const TX_BASELINE = 0;

export const PRO_ADDRESS = 'bitcoincash:qzhwydye09nkk5eaxjwc545ahc2nkgl2lv0h6nft4h';

export const FREE_PRODUCT_LIMIT = 3;
export const FREE_HISTORY_LIMIT = 10;

const PRO_KEY = 'bch-pos:pro-unlock';

export interface ProUnlock {
  txid: string;
  unlockedAt: number;
}

export function loadProUnlock(): ProUnlock | null {
  try {
    const raw = localStorage.getItem(PRO_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as ProUnlock;
  } catch {
    return null;
  }
}

export function saveProUnlock(unlock: ProUnlock): void {
  try {
    localStorage.setItem(PRO_KEY, JSON.stringify(unlock));
  } catch (e) {
    console.warn('Failed to save pro unlock', e);
  }
}

export function isPro(): boolean {
  return loadProUnlock() !== null;
}

const TX_COUNT_CACHE_KEY = 'bch-pos:pro-tx-count';

/** Persist last known tx count to localStorage */
function persistTxCount(count: number): void {
  try { localStorage.setItem(TX_COUNT_CACHE_KEY, JSON.stringify({ count, updatedAt: Date.now() })); }
  catch { /* ignore */ }
}

/** Load last known tx count from localStorage */
export function loadCachedTxCount(): number | undefined {
  try {
    const raw = localStorage.getItem(TX_COUNT_CACHE_KEY);
    if (!raw) return undefined;
    const parsed = JSON.parse(raw) as { count: number; updatedAt: number };
    return parsed.count;
  } catch { return undefined; }
}

/** Fetch the number of Pro purchases from Fulcrum (baseline-adjusted) */
export async function fetchProTxCount(): Promise<number> {
  const count = await fetchTxCount(PRO_ADDRESS);
  const adjusted = Math.max(0, count - TX_BASELINE);
  persistTxCount(adjusted);
  return adjusted;
}

/** Convert a USD target price to BCH, rounded to 6 decimal places */
export function usdToBCH(usd: number, bchUsdPrice: number): number {
  if (bchUsdPrice <= 0) return 0;
  return Math.ceil((usd / bchUsdPrice) * 1e6) / 1e6; // round UP to nearest 0.000001
}

/** Given a tx count and live BCH/USD price, return the correct pricing tier */
export function getPricingTier(txCount: number, bchUsdPrice: number): {
  priceBCH: number;
  priceUSD: number;
  isEarly: boolean;
  spotsLeft: number;
} {
  const spotsLeft = Math.max(0, EARLY_LIMIT - txCount);
  const isEarly = spotsLeft > 0;
  const priceUSD = isEarly ? EARLY_PRICE_USD : REGULAR_PRICE_USD;
  const priceBCH = usdToBCH(priceUSD, bchUsdPrice);
  return { priceBCH, priceUSD, isEarly, spotsLeft };
}
